import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { itineraryAPI } from '../services/api';
import './ItineraryDetail.css';

const TYPE_ICONS = { restaurant: '🍽️', interest: '🎯', wildcard: '🎲' };
const TYPE_LABELS = { restaurant: 'Restaurant', interest: 'Interest', wildcard: 'Wildcard' };
const STEP_LABELS = [
  'Input Received',
  'Interest Mapping',
  'RAG Candidate Search',
  'Slot Assignment',
  'Activity Selection',
  'Plan Assembled',
  'Save Decision',
];

const ActivityCard = ({ activity }) => {
  const type = activity?.type || activity?.activity_type || 'interest';
  return (
    <div className="itin-activity-card">
      <span className={`itin-type-pill ${type}`}>{TYPE_ICONS[type] || '📍'} {TYPE_LABELS[type] || type}</span>
      <div className="itin-activity-name">{activity?.name || activity?.attraction_name || '—'}</div>
      <div className="itin-activity-cat">{activity?.category || ''}</div>
    </div>
  );
};

const DebugStep = ({ index, step }) => {
  const [open, setOpen] = useState(false);
  const status = step?.status || 'ok';
  return (
    <div className={`itin-debug-step ${status}`}>
      <button className="itin-debug-step-header" onClick={() => setOpen(o => !o)}>
        <span className="itin-debug-step-num">Step {index + 1}</span>
        <span className="itin-debug-step-name">{STEP_LABELS[index] || step?.step || `Step ${index + 1}`}</span>
        <span className={`itin-badge ${status}`}>{status}</span>
        {step?.duration_ms != null && (
          <span className="itin-debug-ms">{step.duration_ms}ms</span>
        )}
        <span className="itin-debug-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <pre className="itin-debug-json">
          {JSON.stringify(step?.data ?? step, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ItineraryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await itineraryAPI.getById(id);
        setData(res.data || res);
      } catch {
        setError('Failed to load itinerary detail.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading) return <Layout title="Itinerary Detail"><div className="itin-loading">Loading...</div></Layout>;
  if (error)   return <Layout title="Itinerary Detail"><div className="itin-error">{error}</div></Layout>;

  const itinerary = data?.itinerary || {};
  const rawPayload = itinerary?.payload_json;
  const plan = rawPayload?.plan || rawPayload || {};
  const days = Array.isArray(plan?.days) ? plan.days : [];
  const debugTrace = Array.isArray(data?.generation_log?.steps) ? data.generation_log.steps : [];
  const currentDay = days[activeDay] || {};
  const segments = Array.isArray(currentDay?.segments) ? currentDay.segments : [];
  const meta = itinerary?.generation_metadata || {};
  const hotelRating = meta?.hotel_rating ?? '—';
  const foodPrefs = Array.isArray(meta?.food_preferences)
    ? meta.food_preferences.join(', ')
    : (meta?.food_preferences || '—');

  return (
    <Layout title="Itinerary Detail">
      <div className="itin-detail">
        {/* Back */}
        <button className="itin-back-btn" onClick={() => navigate(-1)}>← Back</button>

        <div className="itin-detail-header">
          <h2>Itinerary #{String(id).slice(0, 8)}…</h2>
          <div className="itin-detail-meta">
            <span className={`itin-type-badge ${(itinerary?.plan_type || 'ai_generated') === 'ai_generated' ? 'ai' : 'preset'}`}>
              {(itinerary?.plan_type || 'ai_generated') === 'ai_generated' ? '🤖 AI Generated' : '🗂️ Preset'}
            </span>
            {itinerary?.created_at && (
              <span className="itin-meta-date">{new Date(itinerary.created_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        <div className="itin-detail-layout">
          {/* LEFT — Itinerary view */}
          <div className="itin-left-panel">
            <h3 className="itin-panel-title">Itinerary Plan</h3>

            {/* Day tabs */}
            {days.length > 0 ? (
              <>
                <div className="itin-day-tabs">
                  {days.map((day, i) => (
                    <button
                      key={i}
                      className={`itin-day-tab ${activeDay === i ? 'active' : ''}`}
                      onClick={() => setActiveDay(i)}
                    >
                      Day {i + 1}
                    </button>
                  ))}
                </div>

                {segments.map(seg => (
                  <div key={seg.name} className="itin-slot-section">
                    <div className="itin-slot-label">
                      {seg.name === 'morning' ? '🌅' : seg.name === 'afternoon' ? '☀️' : '🌙'} {seg.name.charAt(0).toUpperCase() + seg.name.slice(1)}
                    </div>
                    <div className="itin-slot-activities">
                      {(Array.isArray(seg.activities) && seg.activities.length > 0) ? (
                        seg.activities.map((act, i) => <ActivityCard key={i} activity={act} />)
                      ) : (
                        <div className="itin-slot-empty">No activities</div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <pre className="itin-json-preview">{JSON.stringify(plan, null, 2)}</pre>
            )}
          </div>

          {/* RIGHT — Debug pipeline */}
          <div className="itin-right-panel">
            <h3 className="itin-panel-title">Debug Pipeline</h3>
            {debugTrace.length === 0 ? (
              <div className="itin-empty">No debug trace available.</div>
            ) : (
              <div className="itin-debug-steps">
                {debugTrace.map((step, i) => (
                  <DebugStep key={i} index={i} step={step} />
                ))}
              </div>
            )}

            {/* Raw metadata */}
            <div className="itin-meta-section">
              <h4>Metadata</h4>
              <div className="itin-meta-grid">
                <div><span>User</span><strong>{itinerary?.user_id || meta?.user_id || '—'}</strong></div>
                <div><span>Hotel Rating</span><strong>{hotelRating}</strong></div>
                <div><span>Food Pref</span><strong>{foodPrefs}</strong></div>
                <div><span>Days</span><strong>{itinerary?.days_count ?? '—'}</strong></div>
                <div><span>Activities</span><strong>{itinerary?.activities_count ?? '—'}</strong></div>
                <div><span>Plan Type</span><strong>{itinerary?.plan_type || '—'}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ItineraryDetail;
